import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { round } from "lodash";
import { InputBoolean } from "ng-zorro-antd/core";
import { NzTableComponent } from "ng-zorro-antd/table";
import { Page } from "../model/common-type";
import { measureTableColWidth, resolveAutoTableScroll, bindNzTableHeaderBodyScrollSync } from "../utils/hd-table-scroll.util";
import { TotalOption } from "./hd-detail-lines.service";

@Component({
  selector: "hd-detail-lines",
  templateUrl: "./hd-detail-lines.component.html",
  styleUrls: ["./hd-detail-lines.component.less"],
})
export class HdDetailLinesComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild("hdTable", { static: false }) nzTableComponent: NzTableComponent;
  @Input() lines: Array<any>;
  @Input() @InputBoolean() showSelected: boolean = false;
  @Input() @InputBoolean() allowDrop: boolean = false;
  @Input() @InputBoolean() showSearch: boolean = false;
  // 不传递这个字段时默认为查询所有字段是否有匹配的
  @Input() searchFields: string[] = [];
  /**
   * 横向滚动。历史默认 `{ x: '1px' }`（2.1.28 前一直如此）：开启横滚但不锁死列宽，
   * 业务页可不写 th/td width，由内容自适应。传 null 则按容器自动测算。
   */
  @Input() scroll: { [key: string]: string } = { x: "1px" };
  tableScroll: { x?: string; y?: string } = null;
  @Input() selectField: string = "billNumber";
  @Input() selectedField: string;
  /**
   * 行勾选禁用字段名。定义后取当前行该字段：
   * 为 true 时禁用该行勾选（全选也会跳过）。
   */
  @Input() selectDisabledField: string;
  /**
   * 行失效字段名。定义后取当前行该字段：
   * 为 true 时整行背景色为 #F7F8FA、文字 #868A9C。
   */
  @Input() rowInvalidField: string;
  @Output() selectEvent = new EventEmitter();
  @ContentChild("detailLineHead", { static: false })
  detailLineHead: TemplateRef<any>;
  @ContentChild("detailLineBody", { static: false })
  detailLineBody: TemplateRef<any>;
  // 合计区域
  @Input() @InputBoolean() showTotal: boolean = false;
  @Input() totalOption: Array<TotalOption> = [];
  @Input() columnsNumber: number | string = 0;
  @Input() hdFrontPagination: boolean = false;
  totalOptionList = [];

  tableData: Page<any> = new Page();
  tableLoading: boolean = false;
  // 表格分页参数
  tablePageIndex: number = 1;
  @Input() tablePageSize: number = 100;
  // 表格勾选参数
  isAllDisplayDataChecked = false;
  isIndeterminate = false;
  listOfDisplayData = [];
  listOfAllData = [];
  listOfSelection: Array<any> | TemplateRef<void>;
  mapOfCheckedId: { [key: string]: boolean } = {};
  mapOfCheckList: { [key: string]: any } = {};
  filterStr: string;
  /** 表格实际展示的数据（筛选结果或全量） */
  displayLines: Array<any> = [];
  /** 是否处于筛选状态 */
  isFilterData = false;
  storeFormLinesData: any = []; // 缓存的总的列表数据
  /** 控制 nz-table 重建：scroll.y 从无到有时必须重建，否则表头不跟横滚 */
  showTable = true;
  private unbindBodyScroll: (() => void) | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  get effectiveScroll(): { x?: string; y?: string } {
    // 不自动补 nzScroll.y：有 y 会拆表头/表体，无列宽时列错位。
    // 仅有 x 时的纵滚由 less（max-height + overflow-y）承担，表头 sticky。
    // 仅当父组件显式传了 scroll.y 时才走 nz-table 分体纵滚。
    return this.scroll != null ? this.scroll : this.tableScroll;
  }

  ngOnInit(): void {
    this.syncStoreFromLinesInput();
    this.applyDisplayFromStore();
    this.countSelected();
    this.calculateTotal();
  }

  ngAfterViewInit(): void {
    this.scheduleSyncHorizontalScroll();
  }

  ngOnDestroy(): void {
    this.unbindHeaderBodyScrollSync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["lines"]) {
      if (!this.isFilterData) {
        this.syncStoreFromLinesInput();
        this.displayLines = this.lines || [];
      } else if (
        !this.storeFormLinesData ||
        this.storeFormLinesData.length === 0
      ) {
        this.syncStoreFromLinesInput();
      }
    }
    this.countSelected();
    this.calculateTotal();
    if (changes["lines"] || changes["showSelected"]) {
      this.scheduleSyncHorizontalScroll();
    }
  }

  private scheduleSyncHorizontalScroll(retry = 0): void {
    const delay = retry === 0 ? 0 : 50;
    setTimeout(() => {
      if (this.scroll == null) {
        this.syncHorizontalScroll();
      }
      this.bindHeaderBodyScrollSync(0);
      const afterX =
        (this.effectiveScroll && this.effectiveScroll.x) ||
        (this.tableScroll && this.tableScroll.x);
      if (
        this.scroll == null &&
        retry < 5 &&
        this.displayLines &&
        this.displayLines.length > 0 &&
        !afterX &&
        measureTableColWidth(this.nzTableComponent) === 0
      ) {
        this.scheduleSyncHorizontalScroll(retry + 1);
      }
    }, delay);
  }

  private syncHorizontalScroll(): void {
    if (this.scroll != null) {
      return;
    }
    const hasData = !!(this.displayLines && this.displayLines.length);
    const nextScroll = resolveAutoTableScroll(
      this.nzTableComponent,
      hasData,
    );
    const nextX = nextScroll ? nextScroll.x : null;
    // 自动测算只设 x，不设 y（与 2.1.64 前一致，避免无列宽时表头/表体错位）
    if (this.tableScroll && this.tableScroll.x === nextX && !this.tableScroll.y) {
      return;
    }
    const prevHasY = !!(this.tableScroll && this.tableScroll.y);
    this.tableScroll = nextX ? { x: nextX } : null;
    if (prevHasY) {
      this.remountTable();
      return;
    }
    this.cdr.markForCheck();
    this.bindHeaderBodyScrollSync(0);
  }

  /** 重建 nz-table，让 header/body 滚动同步在 AfterViewInit 重新绑定 */
  private remountTable(): void {
    this.unbindHeaderBodyScrollSync();
    setTimeout(() => {
      this.showTable = false;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.showTable = true;
        this.cdr.markForCheck();
        setTimeout(() => this.bindHeaderBodyScrollSync(0));
      });
    });
  }

  /** 表体横滚时同步表头（兜底 ng-zorro 监听丢失）；DOM 未就绪时重试 */
  private bindHeaderBodyScrollSync(retry = 0): void {
    this.unbindHeaderBodyScrollSync();
    if (!this.nzTableComponent || !(this.effectiveScroll && this.effectiveScroll.x)) {
      return;
    }
    const unbind = bindNzTableHeaderBodyScrollSync(this.nzTableComponent);
    if (unbind) {
      this.unbindBodyScroll = unbind;
      return;
    }
    if (retry < 8) {
      setTimeout(() => this.bindHeaderBodyScrollSync(retry + 1), 50);
    }
  }

  private unbindHeaderBodyScrollSync(): void {
    if (this.unbindBodyScroll) {
      this.unbindBodyScroll();
      this.unbindBodyScroll = null;
    }
  }

  private syncStoreFromLinesInput(source?: Array<any>): void {
    const data = source || this.lines;
    this.storeFormLinesData = data ? JSON.parse(JSON.stringify(data)) : [];
  }

  /** 未筛选时与父组件 lines 同源，避免模板内 ngModel 与父数据脱节 */
  private applyDisplayFromStore(): void {
    if (this.lines && this.lines.length > 0) {
      this.displayLines = this.lines;
      return;
    }
    this.displayLines = this.storeFormLinesData
      ? JSON.parse(JSON.stringify(this.storeFormLinesData))
      : [];
  }

  private restoreFromStore(): void {
    this.isFilterData = false;
    this.applyDisplayFromStore();
    this.tableLoading = false;
    this.countSelected();
    this.calculateTotal();
    this.scheduleSyncHorizontalScroll();
  }

  // 全局性的搜索（涵盖页面上所有的可视文字）
  searchLines() {
    // 清空搜索：即使当前展示为空也要能从缓存恢复（对齐 hd-form-lines）
    if (!this.filterStr || !this.filterStr.trim()) {
      if (!this.storeFormLinesData || this.storeFormLinesData.length < 1) {
        return;
      }
      this.tableLoading = true;
      this.restoreFromStore();
      return;
    }
    if (!this.storeFormLinesData || this.storeFormLinesData.length < 1) {
      return;
    }
    this.tableLoading = true;
    const filterValue = this.filterStr.trim().toLowerCase();
    this.isFilterData = true;

    const filteredData = this.storeFormLinesData.filter((data: any) => {
      if (!this.searchFields || this.searchFields.length === 0) {
        for (const item of Object.keys(data)) {
          if (
            data[item] &&
            data[item].toString().toLowerCase().includes(filterValue)
          ) {
            return true;
          }
        }
        return false;
      }
      for (const item of this.searchFields) {
        if (
          data[item] &&
          data[item].toString().toLowerCase().includes(filterValue)
        ) {
          return true;
        }
      }
      return false;
    });
    this.displayLines = JSON.parse(JSON.stringify(filteredData));
    this.tableLoading = false;
    this.countSelected();
    this.calculateTotal();
    this.scheduleSyncHorizontalScroll();
  }

  private countSelected(): void {
    if (
      this.showSelected &&
      this.selectedField &&
      this.displayLines &&
      this.displayLines.length
    ) {
      this.displayLines.forEach((item) => {
        if (item[this.selectedField] === true && !this.isSelectDisabled(item)) {
          this.mapOfCheckedId[item[this.selectField]] = true;
          this.mapOfCheckList[item[this.selectField]] = item;
        }
      });
      if (
        this.displayLines.length === Object.keys(this.mapOfCheckList).length
      ) {
        this.isAllDisplayDataChecked = true;
        this.isIndeterminate = true;
      }
    }
  }

  get LodashRound() {
    return round;
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.displayLines, event.previousIndex, event.currentIndex);
    this.displayLines = JSON.parse(JSON.stringify(this.displayLines));
    if (!this.isFilterData) {
      this.storeFormLinesData = JSON.parse(JSON.stringify(this.displayLines));
    }
  }

  calculateTotal() {
    // 这里处理合计数据
    if (this.showTotal && this.displayLines && this.displayLines.length) {
      this.totalOptionList = new Array(Number(this.columnsNumber) - 1).fill("");
      this.totalOption.forEach((item) => {
        if (item.insertIndex && (item.insertValue || item.insertValue === 0)) {
          this.totalOptionList[item.insertIndex - 1] = {
            value: item.insertValue,
            align: item.align || "right",
            showDecimal: item.showDecimal || false,
            preserveNumber: item.preserveNumber != null ? item.preserveNumber : 2,
            isHide: item.isHide || false,
          };
        }
      });
    }
  }

  // 表格勾选逻辑
  currentPageDataChange($event: any): void {
    this.listOfDisplayData = $event;
    this.refreshStatus();
    this.scheduleSyncHorizontalScroll();
  }

  resetStatus() {
    this.isAllDisplayDataChecked = false;
    this.isIndeterminate = false;
    this.mapOfCheckedId = {};
    this.mapOfCheckList = {};
    this.selectEvent.emit([]);
  }

  refreshStatus(event: boolean = false, data: any = null): void {
    if (data) {
      if (event && this.isSelectDisabled(data)) {
        return;
      }
      this.mapOfCheckedId[data[this.selectField]] = event;
      if (event) {
        !this.mapOfCheckList[data[this.selectField]] &&
          (this.mapOfCheckList[data[this.selectField]] = data);
      } else {
        this.mapOfCheckList[data[this.selectField]] &&
          delete this.mapOfCheckList[data[this.selectField]];
      }
    }
    const selectableList = this.listOfDisplayData.filter(
      (item) => !this.isSelectDisabled(item),
    );
    this.isAllDisplayDataChecked =
      selectableList.length > 0 &&
      selectableList.every((item) => this.mapOfCheckedId[item[this.selectField]]);
    this.isIndeterminate =
      selectableList.some(
        (item) => this.mapOfCheckedId[item[this.selectField]],
      ) && !this.isAllDisplayDataChecked;
    // 触发select事件
    if (this.selectEvent) {
      this.selectEvent.emit(Object.values(this.mapOfCheckList));
    }
  }

  checkAll(value: boolean): void {
    this.listOfDisplayData.forEach((item) => {
      if (this.isSelectDisabled(item)) {
        return;
      }
      if (value) {
        !this.mapOfCheckList[item[this.selectField]] &&
          (this.mapOfCheckList[item[this.selectField]] = item);
      } else {
        this.mapOfCheckList[item[this.selectField]] &&
          delete this.mapOfCheckList[item[this.selectField]];
      }
      this.mapOfCheckedId[item[this.selectField]] = value;
    });
    this.refreshStatus();
  }

  /** 当前行是否禁用勾选 */
  isSelectDisabled(data: any): boolean {
    return !!(
      this.selectDisabledField &&
      data &&
      data[this.selectDisabledField] === true
    );
  }

  /** 当前行是否失效 */
  isInvalidRow(data: any): boolean {
    return !!(
      this.rowInvalidField &&
      data &&
      data[this.rowInvalidField] === true
    );
  }
}
